
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { FilterOption } from '@/components/ui/FilterDropdown';
import { ImageType } from './FilterBar/TypeFilter';
import { useImageFilters } from '@/hooks/image-generator/image-filters';

interface ResourcesContextType {
  // Filter states
  selectedType: ImageType | null;
  styleOptions: FilterOption[];
  selectedStyle: string | null;
  setSelectedStyle: (style: string) => void;
  influenceOptions: FilterOption[];
  selectedInfluence: string | null;
  setSelectedInfluence: (influence: string) => void;
  colorOptions: FilterOption[];
  backgroundOptions: FilterOption[];
  selectedBackground: string | null;
  environmentOptions: FilterOption[];
  moodOptions: FilterOption[];
  imageDescription: string;
  width: string;
  height: string;

  // Filter handlers
  handleTypeChange: (type: ImageType | null) => void;
  handleStyleChange: (options: FilterOption[]) => void;
  handleInfluenceChange: (options: FilterOption[]) => void;
  handleColorChange: (options: FilterOption[]) => void;
  handleBackgroundChange: (options: FilterOption[]) => void;
  handleEnvironmentChange: (options: FilterOption[]) => void;
  handleMoodChange: (options: FilterOption[]) => void;
  handleImageDescriptionChange: (value: string) => void;
  handleWidthChange: (value: string | string[]) => void;
  handleHeightChange: (value: string | string[]) => void;
  
  // Active tab state
  activeTab: 'generate' | 'library';
  setActiveTab: (tab: 'generate' | 'library') => void;
}

// Create context with default values
const ResourcesContext = createContext<ResourcesContextType | undefined>(undefined);

// Provider component
export const ResourcesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Use the useImageFilters hook for filter state and handlers
  const {
    selectedType,
    styleOptions,
    selectedStyle,
    setSelectedStyle,
    influenceOptions,
    selectedInfluence,
    setSelectedInfluence,
    colorOptions,
    backgroundOptions,
    selectedBackground,
    environmentOptions,
    moodOptions,
    handleTypeChange,
    handleStyleChange,
    handleInfluenceChange,
    handleColorChange,
    handleBackgroundChange,
    handleEnvironmentChange,
    handleMoodChange,
    width,
    height,
    setWidth,
    setHeight,
    imageDescription,
    setImageDescription
  } = useImageFilters();

  // Tab state
  const [activeTab, setActiveTab] = useState<'generate' | 'library'>('generate');
  
  // Image description handler
  const handleImageDescriptionChange = (value: string) => {
    setImageDescription(value);
  };
  
  // Width handler
  const handleWidthChange = (value: string | string[]) => {
    if (typeof value === 'string') {
      setWidth(value);
    }
  };

  // Height handler
  const handleHeightChange = (value: string | string[]) => {
    if (typeof value === 'string') {
      setHeight(value);
    }
  };
  
  const value = {
    selectedType,
    styleOptions,
    selectedStyle,
    setSelectedStyle,
    influenceOptions,
    selectedInfluence,
    setSelectedInfluence,
    colorOptions,
    backgroundOptions,
    selectedBackground,
    environmentOptions,
    moodOptions,
    imageDescription,
    width,
    height,
    handleTypeChange,
    handleStyleChange,
    handleInfluenceChange,
    handleColorChange,
    handleBackgroundChange,
    handleEnvironmentChange,
    handleMoodChange,
    handleImageDescriptionChange,
    handleWidthChange,
    handleHeightChange,
    activeTab,
    setActiveTab,
  };

  return (
    <ResourcesContext.Provider value={value}>
      {children}
    </ResourcesContext.Provider>
  );
};

// Custom hook to use the context
export const useResourcesContext = (): ResourcesContextType => {
  const context = useContext(ResourcesContext);
  if (context === undefined) {
    throw new Error('useResourcesContext must be used within a ResourcesProvider');
  }
  return context;
};
